import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';
import MissionDashboard from './components/MissionDashboard';
import DailyTracker from './components/DailyTracker';
import MeetingMinutes from './components/MeetingMinutes';
import WeeklySummaryTab from './components/WeeklySummaryTab';
import AccountabilityBoard from './components/AccountabilityBoard';
import VisionGuide from './components/VisionGuide';
import VisionTour from './components/VisionTour';
import { HiOutlineChevronLeft, HiOutlineRefresh, HiOutlinePlus, HiOutlineCog, HiOutlineQuestionMarkCircle } from 'react-icons/hi';
import './VisionMain.css';

const TOUR_SEEN_KEY = 'v26_tour_seen';

// ─── Small visual mock-ups shown inside tour steps (illustrations only, no data) ──
const exBadge = (text, bg, fg) => (
  <span style={{ background: bg, color: fg, borderRadius: 999, fontSize: 9, fontWeight: 700, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: 0.3 }}>{text}</span>
);

const ExMissionCard = () => (
  <div style={{ border: '1px solid #e2e8f0', borderLeft: '3px solid #a8336b', borderRadius: 8, padding: 10, background: '#fff' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
      <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700 }}>#1 · 2026</span>
      {exBadge('In Progress', '#dbeafe', '#1e40af')}
    </div>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Launch New Website</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: '45%', height: '100%', background: '#a8336b' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 800, color: '#1e293b' }}>45%</span>
    </div>
    <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>🎯 Target: Dec 2026 · 👑 Sarah K.</div>
  </div>
);

const ExTreeRow = ({ depth, color, label, meta }) => (
  <div style={{ marginLeft: depth * 14, display: 'flex', alignItems: 'center', gap: 6, padding: '5px 9px', borderLeft: `3px solid ${color}`, background: '#fff', borderRadius: '0 6px 6px 0', marginBottom: 4 }}>
    <span style={{ fontSize: 11, fontWeight: 600, color: '#1e293b' }}>{label}</span>
    {meta && <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 'auto', whiteSpace: 'nowrap' }}>{meta}</span>}
  </div>
);

const ExRoadmapTree = () => (
  <div>
    <ExTreeRow depth={0} color="#7c3aed" label="🎯 Goal: Redesign homepage" meta="40% weight" />
    <ExTreeRow depth={1} color="#a8336b" label="📌 Action Item: Design mockups" meta="High" />
    <ExTreeRow depth={2} color="#10b981" label="✓ Task: Create wireframe" meta="Sara · 12 Jun" />
    <ExTreeRow depth={2} color="#f59e0b" label="○ Task: Pick colour palette" meta="15 Jun" />
  </div>
);

const ExTaskRow = () => (
  <div style={{ border: '1px solid #f1f5f9', borderRadius: 6, padding: 8, background: '#fff' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', flex: 1 }}>Create wireframe</span>
      {exBadge('In Progress', '#dbeafe', '#1e40af')}
    </div>
    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6 }}>👤 Sara · 📅 due 12 Jun · ☑ 1/2</div>
    <div style={{ fontSize: 11, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 12, height: 12, borderRadius: 3, background: '#16a34a', display: 'inline-block' }} />
      <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>Sketch layout</span>
    </div>
    <div style={{ fontSize: 11, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
      <span style={{ width: 12, height: 12, borderRadius: 3, border: '1.5px solid #cbd5e1', display: 'inline-block' }} />
      <span>Add annotations</span>
    </div>
  </div>
);

const ExMeeting = () => (
  <div style={{ fontSize: 11, color: '#334155' }}>
    <div style={{ marginBottom: 6 }}>{exBadge('Agenda', '#ede9fe', '#5b21b6')} <b style={{ marginLeft: 4 }}>Website progress</b></div>

    {/* Discussion point → Action Item → Tasks */}
    <div style={{ paddingLeft: 8, borderLeft: '2px solid #e2e8f0', marginBottom: 6 }}>
      <div style={{ marginBottom: 5 }}>💬 <b>Discussion:</b> “Homepage mockups need sign-off by Friday.”</div>
      <div style={{ background: '#faf5ff', border: '1px dashed #c084fc', borderRadius: 6, padding: '6px 8px' }}>
        <div>✅ <b>Action Item:</b> Get homepage sign-off <span style={{ color: '#94a3b8' }}>· Sara · High · due Fri</span></div>
        <div style={{ marginTop: 4, paddingLeft: 12, borderLeft: '2px solid #e2e8f0' }}>
          <div>○ <b>Task:</b> Send mockups to client <span style={{ color: '#94a3b8' }}>· Tue</span></div>
          <div>○ <b>Task:</b> Collect feedback <span style={{ color: '#94a3b8' }}>· Thu</span></div>
        </div>
      </div>
    </div>

    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>↳ AI drafted all of this from the recording.</div>
    <div style={{ fontSize: 10, color: '#b45309', background: '#fffaf0', border: '1px solid #fde68a', borderRadius: 6, padding: '5px 8px' }}>
      ✏️ AI got something wrong? Edit any field, or add discussion points, action items & tasks by hand before saving.
    </div>
  </div>
);

// Steps for the guided tour. `tab` switches the active tab before the step shows;
// `selector` points at a real element (via data-tour="…"); missing targets fall
// back to a centered card. `example` renders a small illustration in the tooltip.
const TOUR_STEPS = [
  {
    emoji: '🎯',
    title: 'Welcome to Strategic Vision',
    body: 'A yearly ambition is broken into Missions → Goals → Action Items → Tasks → Subtasks. Tick the small things and the big percentages update on their own. Let’s walk through each tab.'
  },
  {
    tab: 'dashboard',
    selector: '[data-tour="year"]',
    emoji: '📅',
    title: 'Step 1 — Pick the Vision Year',
    body: 'Everything is grouped by year. Select a year here to see its missions. The current year is always available, even before any missions exist.'
  },
  {
    tab: 'dashboard',
    selector: '[data-tour="new-mission"]',
    emoji: '➕',
    title: 'Step 2 — Create a Mission',
    body: 'Click New Mission, then give it a name, target date, a head, and a team. Each mission shows up as a card like this on the board:',
    example: <ExMissionCard />
  },
  {
    tab: 'dashboard',
    emoji: '🗺️',
    title: 'Step 3 — Build the Roadmap',
    body: 'Click any mission card to open its Roadmap, then build the tree top-down: add a Goal (name, owner, deadline) → Action Items inside it (title, priority, responsible) → Tasks (title, deadline, assignees) → Subtasks. Each level carries a weight that drives its parent’s progress. Open the Guide (?) for a field-by-field breakdown of every form:',
    example: <ExRoadmapTree />
  },
  {
    tab: 'daily',
    selector: '[data-tour="daily-header"]',
    emoji: '📋',
    title: 'Daily Tracker — work the tasks',
    body: 'A flat, filterable list for day-to-day work. Update a task’s status inline and tick its subtasks — no need to dig through the whole tree:',
    example: <ExTaskRow />
  },
  {
    tab: 'meetings',
    selector: '[data-tour="meetings-header"]',
    emoji: '🎙️',
    title: 'Meeting Minutes — record & auto-capture',
    body: 'Click Add Minutes, pick a goal & agenda, then record or upload audio. AI transcribes it and, under each discussion point, drafts Action Items and the Tasks beneath them. Nothing is locked — if the AI gets something wrong, edit any field or add items yourself before saving:',
    example: <ExMeeting />
  },
  {
    tab: 'weekly',
    selector: '[data-tour="weekly-header"]',
    emoji: '🗓️',
    title: 'Weekly Summary — report progress',
    body: 'Pick a week to see what was completed, what’s in progress, and what slipped. You can generate an AI summary for status updates and reviews.'
  },
  {
    tab: 'accountability',
    selector: '[data-tour="accountability-header"]',
    emoji: '👥',
    title: 'Accountability — who owns what',
    body: 'A people-centric view gathering every mission head, team member, and action-item owner in one place, with overdue items flagged.'
  },
  {
    tab: 'dashboard',
    selector: '[data-tour="guide-btn"]',
    emoji: '❓',
    title: 'That’s the tour!',
    body: 'Open this Guide button anytime for detailed, step-by-step instructions on every feature — and to replay this walkthrough.'
  }
];

const TABS = [
  { id: 'dashboard',      label: 'Mission Control' },
  { id: 'daily',          label: 'Daily Tracker' },
  { id: 'meetings',       label: 'Meeting Minutes' },
  { id: 'weekly',         label: 'Weekly Summary' },
  { id: 'accountability', label: 'Accountability' }
];

const CreateMissionModal = ({ selectedYear, onClose, onCreated, systemUsers }) => {
  const currentYear = new Date().getFullYear();
  // Fetched here rather than taken from the dashboard list: the previous phase is
  // usually already locked, so it is absent from the board the user is looking at.
  const [phaseOptions, setPhaseOptions] = useState([]);
  const [form, setForm] = useState({
    year: selectedYear || currentYear,
    name: '',
    description: '',
    targetDate: '',
    status: 'Not Started',
    riskLevel: 'Low',
    head: '',
    remarks: '',
    basedOn: '',
    team: []
  });
  const [saving, setSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get(`/api/vision/missions?year=${form.year}&state=all`)
      .then(r => { if (!cancelled) setPhaseOptions(r.data?.data || []); })
      .catch(() => { if (!cancelled) setPhaseOptions([]); });
    return () => { cancelled = true; };
  }, [form.year]);

  const filteredUsers = userSearch.trim()
    ? systemUsers.filter(u =>
        (u.fullName || u.username).toLowerCase().includes(userSearch.toLowerCase()) &&
        !form.team.some(m => m._id === u._id)
      ).slice(0, 8)
    : [];

  const addMember = (user) => {
    if (!user || form.team.some(m => m._id === user._id)) { setUserSearch(''); return; }
    setForm(f => ({ ...f, team: [...f.team, { _id: user._id, fullName: user.fullName, username: user.username }] }));
    setUserSearch('');
  };

  const removeMember = (id) => setForm(f => ({ ...f, team: f.team.filter(m => m._id !== id) }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { alert('Mission name is required.'); return; }
    if (!form.targetDate)  { alert('Target date is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        team: form.team.map(m => m._id),
        head: form.head || undefined,
        basedOn: form.basedOn || undefined
      };
      const { data } = await axiosInstance.post('/api/vision/missions', payload);
      onCreated(data.data);
    } catch (err) {
      console.error('Create mission error:', err);
      alert('Failed to create mission.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="v26-drawer-overlay" onClick={onClose}>
      <div className="v26-drawer" onClick={e => e.stopPropagation()}>
        <div className="v26-drawer-header" style={{ position: 'relative' }}>
          <h2>New Mission</h2>
          <p>Add a new strategic mission</p>
          <button className="v26-drawer-close" onClick={onClose}>×</button>
        </div>
        <div className="v26-drawer-body">
          <div className="v26-form">

            <div className="v26-form-row">
              <div className="v26-form-group" style={{ flex: 1 }}>
                <label>Vision Year *</label>
                <input
                  type="number"
                  min="2020" max="2035"
                  value={form.year}
                  onChange={e => setForm(f => ({ ...f, year: Number(e.target.value) }))}
                />
              </div>
              <div className="v26-form-group" style={{ flex: 1 }}>
                <label>Target Date *</label>
                <input type="date" value={form.targetDate} onChange={e => setForm(f => ({ ...f, targetDate: e.target.value }))} />
              </div>
            </div>

            <div className="v26-form-group">
              <label>Mission Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. New Office Expansion" />
            </div>

            <div className="v26-form-group">
              <label>Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="What does this mission aim to achieve?" />
            </div>

            {/* Records the phase chain. Once the previous phase is locked it is
                hidden from users, and this link can't be reconstructed later. */}
            <div className="v26-form-group">
              <label>Continues From <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <select value={form.basedOn} onChange={e => setForm(f => ({ ...f, basedOn: e.target.value }))}>
                <option value="">— Not a follow-up phase —</option>
                {phaseOptions.map(m => (
                  <option key={m._id} value={m._id}>
                    #{m.order} {m.name}{m.isLocked ? ' (locked)' : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5 }}>
                Pick the earlier mission this one continues, e.g. Phase 1 → Phase 2.
              </p>
            </div>

            <div className="v26-form-row">
              <div className="v26-form-group">
                <label>Mission Head</label>
                <select value={form.head} onChange={e => setForm(f => ({ ...f, head: e.target.value }))}>
                  <option value="">— None —</option>
                  {systemUsers.map(u => <option key={u._id} value={u._id}>{u.fullName || u.username}</option>)}
                </select>
              </div>
              <div className="v26-form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {['Not Started','In Progress','At Risk','On Hold','Completed'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="v26-form-group">
                <label>Risk Level</label>
                <select value={form.riskLevel} onChange={e => setForm(f => ({ ...f, riskLevel: e.target.value }))}>
                  {['Low','Medium','High'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>

            {/* Team Assignment */}
            <div className="v26-form-group">
              <label>Assign Team</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, minHeight: 30 }}>
                {form.team.length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No team members yet</span>
                )}
                {form.team.map(member => (
                  <span key={member._id} className="v26-team-chip">
                    {member.fullName}
                    <button onClick={() => removeMember(member._id)} className="v26-team-chip-remove">×</button>
                  </span>
                ))}
              </div>

              {/* System user search */}
              <div style={{ position: 'relative' }}>
                <input
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search users to add…"
                  style={{ width: '100%', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '9px 12px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
                {filteredUsers.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)',
                    zIndex: 50, overflow: 'hidden', marginTop: 4
                  }}>
                    {filteredUsers.map(u => (
                      <div
                        key={u._id}
                        onClick={() => addMember(u)}
                        style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <span style={{ fontWeight: 600 }}>{u.fullName}</span>
                        <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 11 }}>@{u.username}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="v26-form-group">
              <label>Remarks</label>
              <textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} rows={2} placeholder="Any initial notes…" />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8 }}>
              <button className="v26-btn v26-btn--secondary" onClick={onClose}>Cancel</button>
              <button className="v26-btn v26-btn--primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Creating…' : 'Create Mission'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Vision Settings Modal (coordinator management) ─────────────────────────
const VisionSettingsModal = ({ vision, systemUsers, onClose, onSaved }) => {
  const [coordinators, setCoordinators] = useState(vision?.coordinators || []);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = search.trim()
    ? systemUsers.filter(u =>
        (u.fullName || u.username).toLowerCase().includes(search.toLowerCase()) &&
        !coordinators.some(c => c._id === u._id)
      ).slice(0, 8)
    : [];

  const add = (u) => { setCoordinators(cs => [...cs, { _id: u._id, fullName: u.fullName, username: u.username }]); setSearch(''); };
  const remove = (id) => setCoordinators(cs => cs.filter(c => c._id !== id));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put(`/api/vision/visions/${vision._id}`, {
        coordinators: coordinators.map(c => c._id)
      });
      onSaved(data.data);
    } catch (e) {
      console.error(e);
      alert('Failed to save vision settings');
    } finally { setSaving(false); }
  };

  return (
    <div className="v26-drawer-overlay" onClick={onClose}>
      <div className="v26-drawer" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="v26-drawer-header">
          <h2>Vision Settings</h2>
          <p>Manage coordinators for this vision year</p>
          <button className="v26-drawer-close" onClick={onClose}>×</button>
        </div>
        <div className="v26-drawer-body">
          <div className="v26-form">
            <div className="v26-form-group">
              <label>Vision Coordinators</label>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                Coordinators can view all missions in this year's vision.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, minHeight: 32 }}>
                {coordinators.length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No coordinators assigned</span>
                )}
                {coordinators.map(c => (
                  <span key={c._id} className="v26-team-chip" style={{ background: '#ede9fe', color: '#7c3aed' }}>
                    {c.fullName || c.username}
                    <button onClick={() => remove(c._id)} className="v26-team-chip-remove">×</button>
                  </span>
                ))}
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search users to add as coordinator…"
                  style={{ width: '100%', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '9px 12px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
                {filtered.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)', zIndex: 50, overflow: 'hidden', marginTop: 4 }}>
                    {filtered.map(u => (
                      <div key={u._id} onClick={() => add(u)}
                        style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border-color)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <span style={{ fontWeight: 600 }}>{u.fullName}</span>
                        <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 11 }}>@{u.username}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8 }}>
              <button className="v26-btn v26-btn--secondary" onClick={onClose}>Cancel</button>
              <button className="v26-btn v26-btn--primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VisionMain = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [missions, setMissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [visionData, setVisionData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  // Admin-only view switch: locked missions are hidden from everyone else, and
  // the backend forces 'active' for non-admins regardless of what is sent.
  const [lockState, setLockState] = useState('active');
  const [loading, setLoading] = useState(true);
  const [showCreateMission, setShowCreateMission] = useState(false);
  const [showVisionSettings, setShowVisionSettings] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [systemUsers, setSystemUsers] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [roadmapRefreshKey, setRoadmapRefreshKey] = useState(0);
  const [recomputing, setRecomputing] = useState(false);

  // Fetch system users once for team assignment autocomplete
  useEffect(() => {
    axiosInstance.get('/api/users?limit=200')
      .then(r => setSystemUsers(r.data.users || r.data.data || []))
      .catch(() => {});
  }, []);

  // Auto-launch the guided tour on a user's first visit to this module.
  useEffect(() => {
    let seen = false;
    try { seen = localStorage.getItem(TOUR_SEEN_KEY) === '1'; } catch (_) {}
    if (!seen) {
      const t = setTimeout(() => setRunTour(true), 600); // let the layout settle
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const closeTour = () => {
    setRunTour(false);
    try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch (_) {}
  };

  const startTourFromGuide = () => {
    setShowGuide(false);
    setRunTour(true);
  };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/api/vision/dashboard?year=${selectedYear}&state=${lockState}`);
      if (data.success) {
        setMissions(data.data.missions);
        setStats(data.data.stats);
        setVisionData(data.data.vision);
        setAvailableYears(data.data.availableYears || [selectedYear]);
      }
    } catch (err) {
      console.error('Vision dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, lockState]);

  useEffect(() => { loadDashboard(); }, [loadDashboard, refreshKey]);

  const refresh = () => setRefreshKey(k => k + 1);
  const notifyVisionMutation = useCallback(() => {
    setRoadmapRefreshKey(k => k + 1);
    setRefreshKey(k => k + 1);
  }, []);

  // Patch the dashboard's mission list in place from a mutation's rollup payload,
  // so the card's % and status update instantly without a full refetch.
  const applyRollup = useCallback((rollup) => {
    if (!rollup?.mission) return;
    const fresh = rollup.mission;
    setMissions(prev => prev.map(m => m._id === fresh._id ? { ...m, ...fresh } : m));
  }, []);

  // Admin-only: force a full rollup recompute across every vision/mission/goal/AI/task.
  // Use this if you suspect stored percentages have drifted from reality.
  const recomputeAll = async () => {
    if (!window.confirm('Recompute all vision rollups? This may take a minute on large datasets.')) return;
    setRecomputing(true);
    try {
      await axiosInstance.post('/api/vision/missions/recompute-all');
      refresh();
    } catch (err) {
      console.error('Recompute failed:', err);
      alert(err.response?.data?.message || 'Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  const handleMissionCreated = () => {
    setShowCreateMission(false);
    refresh();
  };

  const handleVisionSaved = (updatedVision) => {
    setVisionData(updatedVision);
    setShowVisionSettings(false);
  };

  // Year selector — always show current year even if no missions
  const currentYear = new Date().getFullYear();
  const yearList = availableYears.includes(currentYear) ? availableYears : [currentYear, ...availableYears].sort((a,b) => b-a);

  // Permission check: admin bypasses all; others need 'create' action on 'vision' card
  const isAdmin = user?.role === 'system_admin';
  const visionActions = user?.permissions?.cardActions?.['vision'] || user?.permissions?.actions || [];
  const canCreateMission = isAdmin || visionActions.includes('approve');

  return (
    <div className="v26-container">

      {/* ── Header ── */}
      <div className="v26-header">
        <div className="v26-header-left">
          <button className="v26-back-btn" onClick={() => navigate('/purpose-selection')} title="Back">
            <HiOutlineChevronLeft size={20} />
          </button>
          <div className="v26-title-block">
            <h1 className="v26-title">Strategic Vision</h1>
            <span className="v26-subtitle">Multi-year mission tracker</span>
            {visionData?.coordinators?.length > 0 && (
              <div className="v26-coordinators" title="Vision Coordinators">
                <span className="v26-coordinators-label">Coordinator{visionData.coordinators.length > 1 ? 's' : ''}:</span>
                {visionData.coordinators.map((c, i) => (
                  <span key={c._id} className="v26-coordinator-chip">
                    {c.fullName || c.username}
                    {i < visionData.coordinators.length - 1 && <span className="v26-coordinator-sep">,</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="v26-header-right">
          {stats && (
            <div className="v26-header-stats" data-tour="stats">
              <div className="v26-hstat v26-hstat--primary">
                <span className="v26-hstat-num">{stats.overallCompletion}%</span>
                <span className="v26-hstat-label">Overall</span>
              </div>
              <div className="v26-hstat v26-hstat--green">
                <span className="v26-hstat-num">{stats.completed}</span>
                <span className="v26-hstat-label">Done</span>
              </div>
              <div className="v26-hstat v26-hstat--warning">
                <span className="v26-hstat-num">{stats.inProgress}</span>
                <span className="v26-hstat-label">Active</span>
              </div>
              <div className="v26-hstat v26-hstat--danger">
                <span className="v26-hstat-num">{stats.atRisk}</span>
                <span className="v26-hstat-label">At Risk</span>
              </div>
            </div>
          )}
          <button
            className="v26-refresh-btn"
            data-tour="guide-btn"
            onClick={() => setShowGuide(true)}
            title="Guide & help"
          >
            <HiOutlineQuestionMarkCircle size={19} />
          </button>
          <button className="v26-refresh-btn" onClick={refresh} title="Refresh">
            <HiOutlineRefresh size={18} />
          </button>
          {isAdmin && (
            <button
              className="v26-refresh-btn"
              onClick={recomputeAll}
              disabled={recomputing}
              title="Recompute all rollups (admin)"
              style={{ opacity: recomputing ? 0.5 : 1 }}
            >
              {recomputing ? '…' : 'Σ'}
            </button>
          )}
        </div>
      </div>

      {/* ── Banner: Year selector + Tabs ── */}
      <div className="v26-banner">
        {/* Year selector row */}
        <div className="v26-year-bar">
          <span className="v26-year-label">Vision Year</span>
          <div className="v26-year-pills" data-tour="year">
            {yearList.map(yr => (
              <button
                key={yr}
                className={`v26-year-pill ${selectedYear === yr ? 'v26-year-pill--active' : ''}`}
                onClick={() => setSelectedYear(yr)}
              >
                {yr}
              </button>
            ))}
          </div>
          {isAdmin && (
            <>
              <span className="v26-lock-divider" />
              <span className="v26-year-label">Show</span>
              <div className="v26-lock-filter" title="Locked missions are closed phases — hidden from every other user">
                {[
                  { id: 'active', label: 'Active' },
                  { id: 'locked', label: '🔒 Locked' },
                  { id: 'all',    label: 'All' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    className={`v26-lock-pill ${lockState === opt.id ? 'v26-lock-pill--active' : ''}`}
                    onClick={() => setLockState(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
          {canCreateMission && (
            <button
              className="v26-btn-new-mission"
              data-tour="new-mission"
              onClick={() => setShowCreateMission(true)}
            >
              <HiOutlinePlus size={15} />
              New Mission
            </button>
          )}
          {isAdmin && visionData && (
            <button
              className="v26-settings-btn"
              onClick={() => setShowVisionSettings(true)}
              title="Vision settings (coordinators)"
            >
              <HiOutlineCog size={18} />
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="v26-tabs" data-tour="tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`v26-tab ${activeTab === tab.id ? 'v26-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="v26-content">
        {loading && activeTab === 'dashboard' && missions.length === 0 ? (
          <div className="v26-loading">Loading missions…</div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <MissionDashboard
                missions={missions}
                stats={stats}
                selectedYear={selectedYear}
                lockState={lockState}
                onMissionUpdate={refresh}
                onRollup={applyRollup}
                user={user}
                systemUsers={systemUsers}
                roadmapRefreshKey={roadmapRefreshKey}
              />
            )}
            {activeTab === 'daily' && <DailyTracker missions={missions} user={user} systemUsers={systemUsers} lockState={lockState} onUpdate={notifyVisionMutation} onRollup={applyRollup} />}
            {activeTab === 'meetings' && <MeetingMinutes missions={missions} user={user} systemUsers={systemUsers} lockState={lockState} onMissionUpdate={refresh} />}
            {activeTab === 'weekly' && <WeeklySummaryTab user={user} missions={missions} />}
            {activeTab === 'accountability' && <AccountabilityBoard missions={missions} user={user} lockState={lockState} />}
          </>
        )}
      </div>

      {showCreateMission && canCreateMission && (
        <CreateMissionModal
          selectedYear={selectedYear}
          systemUsers={systemUsers}
          onClose={() => setShowCreateMission(false)}
          onCreated={handleMissionCreated}
        />
      )}

      {showVisionSettings && visionData && (
        <VisionSettingsModal
          vision={visionData}
          systemUsers={systemUsers}
          onClose={() => setShowVisionSettings(false)}
          onSaved={handleVisionSaved}
        />
      )}

      {showGuide && (
        <VisionGuide
          onClose={() => setShowGuide(false)}
          onStartTour={startTourFromGuide}
        />
      )}

      <VisionTour steps={TOUR_STEPS} isOpen={runTour} onClose={closeTour} onNavigate={setActiveTab} />
    </div>
  );
};

export default VisionMain;
